import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import { classifyPanelFetchError, type PanelFetchError } from "./panel-fetch-error";
import type {
  TDependencySubtype,
  TIssueLink,
  TIssueRelation,
  TIssueRelationTypes,
} from "@/types/issue";

export class IssueRelationsStore {
  relationsByIssueId = observable.map<string, TIssueRelation>();
  linksByIssueId = observable.map<string, TIssueLink[]>();
  loadingByIssueId = observable.map<string, boolean>();
  errorByIssueId = observable.map<string, PanelFetchError | null>();
  private rootStore: RootStore;
  // Two panels on the work item page fetch on mount, and a link can be added or
  // removed while either read is in flight (NG-2616). Only the newest read may
  // write, and a read that started before a link write reads again instead:
  // its list predates the write, and dropping it would hide the links the item
  // already had.
  private fetchSeq = new Map<string, number>();
  private linkWrites = new Map<string, number>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false, fetchSeq: false, linkWrites: false } as never, {
      autoBind: true,
    });
  }

  getRelations(issueId: string): TIssueRelation {
    return (
      this.relationsByIssueId.get(issueId) ?? {
        blocks: [],
        blocked_by: [],
        relates_to: [],
      }
    );
  }

  getLinks(issueId: string): TIssueLink[] {
    return this.linksByIssueId.get(issueId) ?? [];
  }

  async fetchRelationsAndLinks(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
  ): Promise<void> {
    const seq = (this.fetchSeq.get(issueId) ?? 0) + 1;
    this.fetchSeq.set(issueId, seq);
    const linkWritesAtStart = this.linkWrites.get(issueId) ?? 0;
    this.loadingByIssueId.set(issueId, true);
    this.errorByIssueId.set(issueId, null);
    try {
      const [relations, links] = await Promise.all([
        this.rootStore.workItemService.listRelations(workspaceSlug, projectId, issueId),
        this.rootStore.workItemService.listLinks(workspaceSlug, projectId, issueId),
      ]);
      if (this.fetchSeq.get(issueId) !== seq) return;
      if ((this.linkWrites.get(issueId) ?? 0) !== linkWritesAtStart) {
        await this.fetchRelationsAndLinks(workspaceSlug, projectId, issueId);
        return;
      }
      runInAction(() => {
        this.relationsByIssueId.set(issueId, relations);
        this.linksByIssueId.set(issueId, links);
        this.loadingByIssueId.set(issueId, false);
      });
    } catch (error) {
      const kind = classifyPanelFetchError(error);
      const log = kind === "forbidden" ? console.warn : console.error;
      log("Failed to fetch work-item relations", { workspaceSlug, projectId, issueId, error });
      if (this.fetchSeq.get(issueId) !== seq) return;
      runInAction(() => {
        this.loadingByIssueId.set(issueId, false);
        this.errorByIssueId.set(issueId, kind);
      });
    }
  }

  /**
   * Add a single relation and refresh the relations list.
   *
   * "blocks" is not a stored type — we invert it to a `blocked_by` edge on the
   * *related* issue (carrying the same scheduling subtype) so the current issue
   * appears as blocking that item. blocked_by carries the chosen subtype;
   * relates_to carries none.
   */
  async addRelation(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    relationType: TIssueRelationTypes,
    relatedIssueId: string,
    subtype?: TDependencySubtype | null,
    source: AnalyticsSource = "page",
    relatedProjectId?: string,
  ): Promise<void> {
    if (relationType === "blocks") {
      // Send blocked_by from the perspective of the related item.
      await this.rootStore.workItemService.createRelations(
        workspaceSlug,
        relatedProjectId ?? projectId,
        relatedIssueId,
        {
          related_list: [{ relation_type: "blocked_by", related_issue: issueId, subtype }],
        },
      );
    } else {
      await this.rootStore.workItemService.createRelations(workspaceSlug, projectId, issueId, {
        related_list: [{ relation_type: relationType, related_issue: relatedIssueId, subtype }],
      });
    }
    // Before the refresh: the relation exists even if reloading the list fails.
    track("task_relation_added", {
      task_id: issueId,
      project_id: projectId,
      relation_type: relationType,
      source,
    });
    // Refresh so the UI reflects the newly created relation
    await this.fetchRelationsAndLinks(workspaceSlug, projectId, issueId);
  }

  /** Remove a relation and refresh. Mirrors the inverse of addRelation for "blocks". */
  async removeRelation(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    relationType: TIssueRelationTypes,
    relatedIssueId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    if (relationType === "blocks") {
      const peer = this.getRelations(issueId).blocks.find((item) => item.id === relatedIssueId);
      // Incoming edges belong to the peer, which can live in another project.
      await this.rootStore.workItemService.removeRelation(
        workspaceSlug,
        peer?.project_id ?? projectId,
        relatedIssueId,
        issueId,
        "blocked_by" as never,
      );
    } else {
      await this.rootStore.workItemService.removeRelation(
        workspaceSlug,
        projectId,
        issueId,
        relatedIssueId,
        relationType as never,
      );
    }
    track("task_relation_removed", {
      task_id: issueId,
      project_id: projectId,
      relation_type: relationType,
      source,
    });
    await this.fetchRelationsAndLinks(workspaceSlug, projectId, issueId);
  }

  async addLink(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    data: Pick<TIssueLink, "url" | "title">,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const link = await this.rootStore.workItemService.createLink(
      workspaceSlug,
      projectId,
      issueId,
      data,
    );
    runInAction(() => {
      this.linkWrites.set(issueId, (this.linkWrites.get(issueId) ?? 0) + 1);
      const existing = this.linksByIssueId.get(issueId) ?? [];
      this.linksByIssueId.set(issueId, [...existing, link]);
    });
    // Ids only: a link's url and title are what the user typed.
    track("task_link_added", { task_id: issueId, project_id: projectId, link_id: link.id, source });
  }

  async removeLink(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    linkId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.workItemService.deleteLink(workspaceSlug, projectId, issueId, linkId);
    runInAction(() => {
      this.linkWrites.set(issueId, (this.linkWrites.get(issueId) ?? 0) + 1);
      const existing = this.linksByIssueId.get(issueId) ?? [];
      this.linksByIssueId.set(
        issueId,
        existing.filter((l) => l.id !== linkId),
      );
    });
    track("task_link_removed", {
      task_id: issueId,
      project_id: projectId,
      link_id: linkId,
      source,
    });
  }
}
